import { useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Me } from '@replay/shared';
import { api, errorMessage } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { Logo } from '../components/Icon.tsx';
import './auth/auth.css';

export function AuthLayout({ children, title, lead }: { children: React.ReactNode; title: string; lead?: React.ReactNode }) {
  return (
    <div className="auth auth-console">
      <div className="auth-intro" aria-hidden="true">
        <span className="auth-overline">Tu próxima partida te espera</span>
        <p>
          Vuelve a<br />
          sentirlo.
        </p>
        <span className="auth-intro-copy">
          Los juegos de siempre.
          <br />
          Tu lugar para volver.
        </span>
        <div className="auth-signal">
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      <div className="auth-card">
        <div className="wordmark">
          <Logo />
          REPLAY
        </div>
        <h1>{title}</h1>
        {lead && <p className="muted">{lead}</p>}
        {children}
      </div>
      <p className="auth-foot faint small">Plataforma privada. Solo con invitación.</p>
    </div>
  );
}

export function Login() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Second step (two-step verification): the password was right, a code is needed.
  const [mfa, setMfa] = useState<{ token: string; recovery: boolean } | null>(null);
  const [code, setCode] = useState('');
  const next = params.get('next') || '/';

  if (me) return <Navigate to={next.startsWith('/') ? next : '/'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ me?: Me; mfaRequired?: boolean; mfaToken?: string }>('/api/auth/login', { body: { username, password } });
      if (r.mfaRequired && r.mfaToken) {
        setPassword('');
        setMfa({ token: r.mfaToken, recovery: false });
        return;
      }
      qc.setQueryData(['me'], r.me);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ me: Me }>('/api/auth/login/mfa', { body: { mfaToken: mfa.token, code } });
      qc.setQueryData(['me'], r.me);
    } catch (err) {
      setError(errorMessage(err));
      if ((err as { code?: string }).code === 'mfa_expired') setMfa(null);
    } finally {
      setBusy(false);
    }
  };

  if (mfa)
    return (
      <AuthLayout title="Verificación en dos pasos" lead={mfa.recovery ? 'Escribe uno de tus códigos de recuperación.' : 'Escribe el código de 6 cifras de tu app de verificación.'}>
        <form className="form-grid" onSubmit={submitCode}>
          <label className="field">
            <span>{mfa.recovery ? 'Código de recuperación' : 'Código de verificación'}</span>
            <input
              className={`input${mfa.recovery ? '' : ' code-input'}`}
              inputMode={mfa.recovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              maxLength={mfa.recovery ? 20 : 6}
              value={code}
              onChange={(e) => setCode(mfa.recovery ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              autoFocus
              data-autofocus
            />
          </label>
          {error && (
            <div className="error-box" role="alert">
              {error}
            </div>
          )}
          <button className="btn btn-primary btn-lg" disabled={busy}>
            {busy ? 'Comprobando…' : 'Entrar'}
          </button>
        </form>
        <div className="row" data-nav-row>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setMfa({ ...mfa, recovery: !mfa.recovery });
              setCode('');
              setError(null);
            }}
          >
            {mfa.recovery ? 'Usar la app de verificación' : 'Usar un código de recuperación'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMfa(null)}>
            Volver
          </button>
        </div>
      </AuthLayout>
    );

  return (
    <AuthLayout title="Entra a jugar">
      <form className="form-grid" onSubmit={submit}>
        <label className="field">
          <span>Usuario</span>
          <input className="input" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus data-autofocus />
        </label>
        <label className="field">
          <span>Contraseña</span>
          <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <button className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
      <Link to="/forgot">¿Has olvidado tu contraseña?</Link>
    </AuthLayout>
  );
}
